import { filterSettings, HEADERS } from '@config/game.js'
import { computed, nextTick, watch, ref } from 'vue'
import { Duration } from 'luxon'
import {
  GAME_PLATFORM_LABELS,
  STATUS_NOT_ACCEPTED,
  STATUS_ACCEPTED,
  STATUS_DELETED,
  VISIBLE_HIDDEN,
  VISIBLE_PUBLIC,
} from '@config/options.js'
import {
  MOD_FREE_AND_PAID_WEIGHTED,
  MOD_FREE,
  MOD_PAID,
} from '@config/monetization.js'
import {
  isObjEmpty,
  secsToMs,
  isArray,
  isEqual,
  clone,
} from '@helpers/utils.js'

export const FOLLOWED_COLLECTIONS_ID = 'followed-collections'
export const GUIDE_FILTER_OPTION_ID = 'guide-filter'
export const SUBSCRIBED_MODS_ID = 'subscribed-mods'
export const CONTENT_TYPE_FILTER = 'content_type'
export const ADMIN_FILTER_OPTION = 'admin_options'
export const PLATFORM_OPTION = 'platforms'
export const DATE_FILTER = 'date_live'

const PENDING_FILTER = { text: 'Show pending', value: 'pending' }
const DELETED_FILTER = { text: 'Show archived', value: 'archived' }
const HIDDEN_FILTER = { text: 'Show hidden', value: 'hidden' }

const ADMIN_FILTERS = [HIDDEN_FILTER, PENDING_FILTER, DELETED_FILTER]
const ADMIN_FILTERS_GUIDES = [PENDING_FILTER, DELETED_FILTER]

const CONTENT_TYPE_FILTER_ITEMS = ['free', 'premium']

// Maps the revenue_type URL value to its content_type filter item
export const REVENUE_TYPE_MAP = {
  [MOD_FREE]: CONTENT_TYPE_FILTER_ITEMS[0],
  [MOD_PAID]: CONTENT_TYPE_FILTER_ITEMS[1],
}

export const DATE_FILTER_ITEMS = ['today', 'week', 'month', '3_months', 'year']

// Maps the date_live URL value to its timespan filter item
export const DATE_LIVE_MAP = {
  last_day: DATE_FILTER_ITEMS[0],
  last_week: DATE_FILTER_ITEMS[1],
  last_month: DATE_FILTER_ITEMS[2],
  last_3_months: DATE_FILTER_ITEMS[3],
  last_year: DATE_FILTER_ITEMS[4],
}

const CONTENT_FILTER_ITEM = {
  id: CONTENT_TYPE_FILTER,
  hideInTagsForm: true,
  name: CONTENT_TYPE_FILTER,
  name_key: CONTENT_TYPE_FILTER,
  selected: [],
  tags: CONTENT_TYPE_FILTER_ITEMS,
  keys: CONTENT_TYPE_FILTER_ITEMS,
  type: 'dropdown',
}

const DATE_FILTER_ITEM = {
  id: DATE_FILTER,
  hideInTagsForm: true,
  name: DATE_FILTER,
  name_key: DATE_FILTER,
  selected: [],
  tags: DATE_FILTER_ITEMS,
  keys: Object.keys(DATE_LIVE_MAP),
  type: 'dropdown',
}

export const COLLECTION_FILTER_ITEMS = [DATE_FILTER_ITEM]

export const filterMenuOpen = computed(() => filterMenuOpenState.value)

const filterMenuOpenState = ref(true)
const loadingRef = ref({})
const state = ref({})

export default function (id, { tagsForm } = {}) {
  if (!id) {
    throw new Error('useFilter requires id')
  }
  if (loadingRef.value[id] === undefined) {
    loadingRef.value[id] = true
  }

  const filter = computed(() =>
    (state.value[id] || []).filter((f) => !tagsForm || !f.hideInTagsForm)
  )

  const loading = computed(() => loadingRef.value[id])

  const activeSelectedFilters = computed(() => _getActiveTags(filter.value))

  const activeExcludedFilters = computed(() =>
    _getActiveTags(filter.value, false)
  )

  const activeFiltersCount = computed(
    () =>
      activeSelectedFilters.value.length + activeExcludedFilters.value.length
  )

  const getFilter = computed(() => (state.value[id] ? _getQueryObj() : null))

  const settingsTags = computed(() =>
    filterSettings.value.tags && id !== SUBSCRIBED_MODS_ID
      ? filterSettings.value.tags.toLowerCase().split(',')
      : []
  )

  function updateFilter(option) {
    if (settingsTags.value.includes(option.value?.toLowerCase())) {
      // Don't allow deselecting query param tag
      return
    }

    if (option.id === PLATFORM_OPTION) {
      const platform = Object.entries(GAME_PLATFORM_LABELS).find(
        ([, value]) => value === option.value
      )[0]

      HEADERS.platform = HEADERS.platform === platform ? null : platform
    }

    const updateTags = [...state.value[id]]
    const index = updateTags.findIndex(
      (tagOption) => tagOption.id === option.id
    )

    if (index >= 0) {
      const tag = { ...updateTags[index] }
      const filterIn = option.filterIn ?? true

      if (filterIn) {
        switch (option.type) {
          case 'dropdown':
            tag.selected = _handleDropboxes(tag.selected, option)
            break
          case 'checkboxes':
            tag.selected = _handleCheckboxes(tag.selected, option)
            break
        }

        if (tag.excluded) {
          tag.excluded = _dedupeFilterTags(tag.excluded, option)
        }
      } else {
        if (tag.excluded) {
          tag.excluded = _handleCheckboxes(tag.excluded, option)
        }

        tag.selected = _dedupeFilterTags(tag.selected, option)
      }

      if (option.showMore) {
        tag.showMore = !tag.showMore
      }

      updateTags.splice(index, 1, tag)
    }

    if (!isEqual(state.value[id], updateTags)) {
      state.value[id] = updateTags
    }
  }

  function resetFilter() {
    const newFilters = state.value?.[id]?.map((filter) => {
      const selected = []

      settingsTags.value.forEach((queryTag) => {
        const matchingTag = filter.tags.find(
          (tag) => tag.toLowerCase() === queryTag
        )
        if (matchingTag) {
          selected.push(matchingTag)
        }
      })

      if (filter.excluded) filter.excluded = []

      return {
        ...filter,
        selected,
      }
    })

    if (newFilters.some((f) => f.id === PLATFORM_OPTION)) {
      HEADERS.platform = null
    }

    state.value[id] = newFilters || []
  }

  function setFilter(options, keepExisting = true) {
    nextTick(() => {
      // set loadingRef to false after the next dom update
      loadingRef.value[id] = false
      const result = clone(options)

      if (keepExisting) {
        result.forEach((option) => {
          const match = state.value[id]?.find((x) => x.id === option.id)

          if (match?.selected && !option.selected.length) {
            option.selected = match.selected
          }

          if (match?.excluded && !option.excluded.length) {
            option.excluded = match.excluded
          }

          if (match?.showMore) {
            option.showMore = true
          }
        })
      }

      state.value[id] = isArray(result) ? result : []
    })
  }

  function inMemoryFilter(items) {
    let resultsCopy = items && clone(items)

    if (activeSelectedFilters.value.length > 0) {
      activeSelectedFilters.value
        .filter((f) => ![ADMIN_FILTER_OPTION, PLATFORM_OPTION].includes(f.id))
        .forEach(({ tag, id }) => {
          if (id === DATE_FILTER) {
            resultsCopy = resultsCopy.filter((item) => {
              const dur = Duration.fromMillis(
                Date.now() - secsToMs(item.date_live)
              )
              switch (tag) {
                case DATE_FILTER_ITEMS[0]:
                  return dur.as('days') <= 1
                case DATE_FILTER_ITEMS[1]:
                  return dur.as('days') <= 7
                case DATE_FILTER_ITEMS[2]:
                  return dur.as('months') <= 1
                case DATE_FILTER_ITEMS[3]:
                  return dur.as('months') <= 3
                case DATE_FILTER_ITEMS[4]:
                  return dur.as('years') <= 1
              }
            })
          } else if (id === CONTENT_TYPE_FILTER) {
            switch (tag) {
              case CONTENT_TYPE_FILTER_ITEMS[0]:
                resultsCopy = resultsCopy.filter((item) => !item.price)
                break
              case CONTENT_TYPE_FILTER_ITEMS[1]:
                resultsCopy = resultsCopy.filter((item) => item.price)
                break
            }
          } else {
            resultsCopy = resultsCopy.filter((item) => {
              const tagPropertyName = item.collection_tags
                ? 'collection_tags'
                : 'tags'
              return (
                !item[tagPropertyName] ||
                item[tagPropertyName].find(
                  (itemTag) =>
                    (itemTag.name || itemTag).toLowerCase() ===
                    tag.toLowerCase()
                )
              )
            })
          }
        })
    }

    resultsCopy = resultsCopy.filter((item) => {
      const tagPropertyName = item.collection_tags ? 'collection_tags' : 'tags'
      return !item[tagPropertyName]?.some((itemTag) =>
        activeExcludedFilters.value.some(
          (t) => t.tag === (itemTag?.name || itemTag)
        )
      )
    })

    return resultsCopy ? resultsCopy : []
  }

  function watchFilters(callback, immediate = true) {
    watch(
      filter,
      (value) => {
        callback(value)
      },
      { immediate }
    )
  }

  function _handleDropboxes(tags, option) {
    return tags.includes(option.value) ? [] : [option.value]
  }

  function _handleCheckboxes(tags, option) {
    return tags.includes(option.value)
      ? tags.filter((item) => item !== option.value)
      : [...tags, option.value]
  }

  function _dedupeFilterTags(tags, option) {
    return tags.filter((t) => t !== option.value)
  }

  function _getActiveTags(filters, filterIn = true) {
    const result = []
    const key = filterIn ? 'selected' : 'excluded'

    filters
      .filter((filter) => filter[key]?.length)
      .forEach((filtered) => {
        filtered[key].forEach((tagName) => {
          const tag = {
            id: filtered.id,
            filter: filtered.name,
            tag: tagName,
            filterIn,
            type: filtered.type,
          }

          result.push(tag)
        })
      })

    return result
  }

  function _getQueryObj() {
    if (activeFiltersCount.value === 0) {
      return {}
    }

    const selected = _getActiveTags(filter.value)
    const excluded = _getActiveTags(filter.value, false)

    const queryObj = {}
    queryObj.tags = selected
      .filter(
        (_selected) =>
          ![
            ADMIN_FILTER_OPTION,
            CONTENT_TYPE_FILTER,
            PLATFORM_OPTION,
            DATE_FILTER,
          ].includes(_selected.id)
      )
      .map((_selected) => _selected.tag)
      .join(',')

    queryObj['tags-not-in'] = excluded.map((t) => t.tag).join(',')

    queryObj.admin = selected
      .filter((_selected) => _selected.id === ADMIN_FILTER_OPTION)
      .map(
        (_selected) =>
          ADMIN_FILTERS.find((_filter) => _filter.text === _selected.tag).value
      )
      .join(',')

    queryObj.platforms = selected
      .filter((_selected) => _selected.id === PLATFORM_OPTION)
      .map(
        (_selected) =>
          Object.entries(GAME_PLATFORM_LABELS).find(
            ([, value]) => value === _selected.tag
          )[0]
      )
      .join(',')

    queryObj.timespan = selected.find(
      (_selected) => _selected.id === DATE_FILTER
    )?.tag

    queryObj.content_type = selected.find(
      (_selected) => _selected.id === CONTENT_TYPE_FILTER
    )?.tag

    if (!queryObj.admin) delete queryObj.admin

    return queryObj
  }

  return {
    activeSelectedFilters,
    activeExcludedFilters,
    activeFiltersCount,
    inMemoryFilter,
    settingsTags,
    updateFilter,
    watchFilters,
    resetFilter,
    setFilter,
    getFilter,
    loading,
    filter,
  }
}

export function toggleFilterMenu() {
  filterMenuOpenState.value = !filterMenuOpenState.value
}

export function normalizeModFilters(
  options,
  filterQuery,
  useAdminFilters,
  { marketplaceEnabled } = {}
) {
  const filters = [DATE_FILTER_ITEM, ...options]

  if (marketplaceEnabled) {
    filters.unshift(CONTENT_FILTER_ITEM)
  }

  if (useAdminFilters) {
    filters.unshift({
      id: ADMIN_FILTER_OPTION,
      hideInTagsForm: true,
      name: ADMIN_FILTER_OPTION,
      name_key: ADMIN_FILTER_OPTION,
      selected: [],
      tags: ADMIN_FILTERS.map((f) => f.text),
      type: 'checkboxes',
    })
  }

  return filterQuery && !isObjEmpty(filterQuery)
    ? _mergeFilterQuery(filters, filterQuery)
    : filters
}

export function normalizeCollectionFilters(
  options,
  filterQuery,
  useAdminFilters
) {
  const filters = [...COLLECTION_FILTER_ITEMS, ...options]
  if (useAdminFilters) {
    filters.unshift({
      id: ADMIN_FILTER_OPTION,
      hideInTagsForm: true,
      name: ADMIN_FILTER_OPTION,
      name_key: ADMIN_FILTER_OPTION,
      selected: [],
      tags: ADMIN_FILTERS.map((f) => f.text),
      type: 'checkboxes',
    })
  }

  return filterQuery && !isObjEmpty(filterQuery)
    ? _mergeFilterQuery(filters, filterQuery)
    : filters
}

export function normalizeGuideFilters(
  options,
  filterQuery,
  useAdminFilters,
  allTags,
  showMore
) {
  const filters = [
    DATE_FILTER_ITEM,
    _getGuideFilterOptions(options, allTags, showMore),
  ]

  if (useAdminFilters) {
    filters.unshift({
      id: ADMIN_FILTER_OPTION,
      hideInTagsForm: true,
      statusOption: true,
      name: ADMIN_FILTER_OPTION,
      name_key: ADMIN_FILTER_OPTION,
      selected: [],
      tags: ADMIN_FILTERS_GUIDES.map((f) => f.text),
      type: 'checkboxes',
    })
  }

  return filterQuery && !isObjEmpty(filterQuery)
    ? _mergeFilterQuery(filters, filterQuery)
    : filters
}

function _getGuideFilterOptions(filters, allTags, showMore) {
  const tags = filters.filter((n) => n.length)

  return {
    id: GUIDE_FILTER_OPTION_ID,
    hidden: false,
    name: 'Tags',
    name_key: 'tags',
    selected: [],
    excluded: [],
    tags,
    type: 'checkboxes',
    allTags,
    showMore,
  }
}

export function getDateFilter(query) {
  const queryValue = query.timespan ?? DATE_LIVE_MAP[query.date_live]
  delete query.date_live

  if (!DATE_FILTER_ITEMS.includes(queryValue)) {
    delete query.timespan
    return query
  }

  query.timespan = queryValue

  return query
}

export function getContentTypeFilter(query) {
  if (!query.content_type) return query

  switch (query.content_type) {
    case CONTENT_TYPE_FILTER_ITEMS[0]:
      query.revenue_type = MOD_FREE
      break
    case CONTENT_TYPE_FILTER_ITEMS[1]:
      query.revenue_type = MOD_PAID
      break
    default:
      query.revenue_type = MOD_FREE_AND_PAID_WEIGHTED
  }

  delete query.content_type

  return query
}

function _mergeFilterQuery(options, filterQuery) {
  const filters = clone(options)

  Object.entries(filterQuery).forEach(([key, queryString]) => {
    const tags = queryString.split(',').map((t) => t.toLowerCase())
    const queryValue = queryString.split(',')[0]

    switch (key) {
      case 'tags':
        filters.forEach((f) => {
          if (f.id.startsWith('T-') || f.id === GUIDE_FILTER_OPTION_ID) {
            f.tags.forEach((optionTag) => {
              if (tags.includes(optionTag.toLowerCase())) {
                f.selected.push(optionTag)
              }
            })
          }
        })
        break

      case 'tags-not-in':
        filters.forEach((f) => {
          if (f.id.startsWith('T-') || f.id === GUIDE_FILTER_OPTION_ID) {
            f.tags.forEach((optionTag) => {
              if (tags.includes(optionTag.toLowerCase())) {
                f.excluded.push(optionTag)
              }
            })
          }
        })
        break

      case 'admin': {
        const filter = filters.find((f) => f.id === ADMIN_FILTER_OPTION)
        if (filter) {
          filter.selected.push(
            ...ADMIN_FILTERS.filter((a) => tags.includes(a.value)).map(
              (a) => a.text
            )
          )
        }
        break
      }

      case 'date_live': {
        const filter = filters.find((f) => f.id === DATE_FILTER)
        if (filter && DATE_FILTER_ITEMS.includes(queryValue)) {
          filter.selected = [queryValue]
        }
        break
      }

      case 'revenue_type':
      case 'content_type': {
        const filter = filters.find((f) => f.name === CONTENT_TYPE_FILTER)
        if (filter) {
          filter.selected =
            key === 'revenue_type'
              ? [REVENUE_TYPE_MAP[queryValue]]
              : [queryValue]
        }
        break
      }
    }
  })

  return filters
}

export function getAPIFilter(query) {
  if (!query.admin) return query

  query['status-in'] = [STATUS_ACCEPTED]

  query.admin.split(',').forEach((q) => {
    switch (q) {
      case HIDDEN_FILTER.value:
        query['visible-in'] = `${VISIBLE_HIDDEN},${VISIBLE_PUBLIC}`
        break

      case PENDING_FILTER.value:
        query['status-in'].unshift(STATUS_NOT_ACCEPTED)
        break

      case DELETED_FILTER.value:
        query['status-in'].push(STATUS_DELETED)
        break
    }
  })

  if (query['status-in'].length > 1) {
    query['status-in'] = query['status-in'].join(',')
  } else {
    delete query['status-in']
  }
  delete query.admin
  return query
}
